import { useState } from 'react'
import type { ReactNode } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { X } from '@/lib/icons'
import type { PayoutListItem } from '@ayub/shared'
import { formatNumber, formatSom, PayoutType } from '@ayub/shared'
import { Button } from '@/components/ui/button'
import { ConfirmDialog } from '@/components/ui/confirm-dialog'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'
import { PAYOUTS_PAGE_SIZE, cancelPayout, fetchPayoutCard, markPayoutPaid } from './api'
import { CancelPayoutDialog } from './CancelPayoutDialog'
import { PayoutsNeutralEmptyState } from './EmptyStates'
import { PayoutsErrorBanner, PayoutsRetryState } from './ErrorStates'
import { invalidatePayoutRelatedCaches, usePayoutRequests } from './hooks'
import { Pagination } from './Pagination'
import { PayoutCardCell } from './PayoutCardCell'
import { PAYOUT_TYPE_LABELS } from './statusMeta'

export interface PayoutRequestsViewProps {
  page: number
  onPageChange: (page: number) => void
}

/**
 * "💸 So'rovlar" tabi — `docs/03-app-flow.md` Sahifa 4: bloger o'zi pul so'ragan, hali
 * to'lanmagan. Har qator: ism, summa, maskalangan karta + 👁, `[To'ladim]` / `[✖️ Bekor]`.
 */
export function PayoutRequestsView({ page, onPageChange }: PayoutRequestsViewProps) {
  const queryClient = useQueryClient()
  const list = usePayoutRequests(page)

  const [markPaidTarget, setMarkPaidTarget] = useState<PayoutListItem | null>(null)
  const [cancelTarget, setCancelTarget] = useState<PayoutListItem | null>(null)

  const markPaidMutation = useMutation<PayoutListItem, Error, number>({
    mutationFn: (id) => markPayoutPaid(id, {}),
    onSuccess: () => {
      invalidatePayoutRelatedCaches(queryClient)
      setMarkPaidTarget(null)
    },
  })

  const cancelMutation = useMutation<PayoutListItem, Error, { id: number; note: string }>({
    mutationFn: ({ id, note }) => cancelPayout(id, { note }),
    onSuccess: () => {
      invalidatePayoutRelatedCaches(queryClient)
      setCancelTarget(null)
    },
  })

  if (list.isLoading) return <PayoutRequestsSkeleton />

  if (list.error && !list.data) {
    return <PayoutsRetryState error={list.error} onRetry={() => void list.refetch()} />
  }

  const items = list.data?.items ?? []
  const total = list.data?.total ?? 0

  if (total === 0) {
    return <PayoutsNeutralEmptyState message="Hozircha to'lov so'ragan bloger yo'q." />
  }

  return (
    // `aria-busy` — fon rejimida qayta so'ralayotganda skrinriderga signal beradi.
    <div className="flex flex-col gap-3" aria-busy={list.isFetching}>
      {list.error && (
        <PayoutsErrorBanner
          error={list.error}
          isFetching={list.isFetching}
          onRetry={() => void list.refetch()}
        />
      )}

      {/* Desktop / tablet: jadval */}
      <div className="hidden overflow-x-auto rounded-card border border-app-border sm:block">
        <table className="w-full border-collapse text-sm">
          <thead className="sticky top-0 bg-app-card">
            <tr>
              <Th>Bloger</Th>
              <Th>Summa</Th>
              <Th>Karta</Th>
              <Th />
            </tr>
          </thead>
          <tbody>
            {items.map((payout) => (
              <tr key={payout.id} className="border-b border-app-border last:border-b-0">
                <td className="px-3 py-2">
                  <div className="flex flex-col">
                    <span className="font-medium text-app-text">{payout.bloggerName}</span>
                    <span className="text-xs text-app-hint">@{payout.instagramUsername}</span>
                    {payout.type !== PayoutType.ON_REQUEST && (
                      <span className="text-xs text-app-hint">
                        {PAYOUT_TYPE_LABELS[payout.type]}
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-3 py-2">
                  <span className="tabular font-medium text-brand">{formatSom(payout.amount)}</span>
                </td>
                <td className="px-3 py-2">
                  <PayoutCardCell
                    maskedCard={payout.cardNumberMasked}
                    onReveal={() => fetchPayoutCard(payout.id)}
                  />
                </td>
                <td className="px-3 py-2">
                  <div className="flex items-center justify-end gap-2">
                    <Button size="sm" variant="danger" onClick={() => setCancelTarget(payout)}>
                      <X size={14} aria-hidden="true" />
                      Bekor
                    </Button>
                    <Button size="sm" onClick={() => setMarkPaidTarget(payout)}>
                      To&apos;ladim
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobil: kartochkalar ro'yxati */}
      <div className="flex flex-col gap-2 sm:hidden">
        {items.map((payout) => (
          <div
            key={payout.id}
            className="flex flex-col gap-2 rounded-card border border-app-border bg-app-card p-3"
          >
            <div className="flex items-start justify-between gap-2">
              <div className="flex flex-col">
                <span className="font-medium text-app-text">{payout.bloggerName}</span>
                <span className="text-xs text-app-hint">@{payout.instagramUsername}</span>
              </div>
              <span className="tabular font-semibold text-brand">{formatSom(payout.amount)}</span>
            </div>
            <PayoutCardCell
              maskedCard={payout.cardNumberMasked}
              onReveal={() => fetchPayoutCard(payout.id)}
            />
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="danger"
                className="flex-1"
                onClick={() => setCancelTarget(payout)}
              >
                <X size={14} aria-hidden="true" />
                Bekor
              </Button>
              <Button size="sm" className="flex-1" onClick={() => setMarkPaidTarget(payout)}>
                To&apos;ladim
              </Button>
            </div>
          </div>
        ))}
      </div>

      <Pagination
        page={page}
        pageSize={PAYOUTS_PAGE_SIZE}
        total={total}
        onPageChange={onPageChange}
      />

      {/* "To'ladim" tasdig'i — `docs/03-app-flow.md` matni so'zma-so'z: summa, ism, video soni. */}
      <ConfirmDialog
        open={markPaidTarget !== null}
        title="To'lovni tasdiqlaysizmi?"
        description={
          markPaidTarget && (
            <>
              <span className="font-medium text-app-text">{markPaidTarget.bloggerName}</span>ga{' '}
              <span className="tabular font-medium text-app-text">
                {formatSom(markPaidTarget.amount)}
              </span>{' '}
              to&apos;ladingizmi? Bu amal blogerning balansini 0 ga tushiradi va uning{' '}
              <span className="font-medium text-app-text">
                {formatNumber(markPaidTarget.videoCount)} ta
              </span>{' '}
              videosini &quot;To&apos;landi&quot;ga o&apos;tkazadi. Bu amalni ORQAGA QAYTARIB
              BO&apos;LMAYDI.
            </>
          )
        }
        confirmLabel="Ha, to'ladim"
        isLoading={markPaidMutation.isPending}
        errorMessage={markPaidMutation.error?.message}
        onConfirm={() => markPaidTarget && markPaidMutation.mutate(markPaidTarget.id)}
        onCancel={() => setMarkPaidTarget(null)}
      />

      <CancelPayoutDialog
        open={cancelTarget !== null}
        bloggerName={cancelTarget?.bloggerName ?? ''}
        amount={cancelTarget?.amount ?? 0}
        isSubmitting={cancelMutation.isPending}
        errorMessage={cancelMutation.error?.message}
        onCancel={() => setCancelTarget(null)}
        onSubmit={(note) => cancelTarget && cancelMutation.mutate({ id: cancelTarget.id, note })}
      />
    </div>
  )
}

function Th({ children }: { children?: ReactNode }) {
  return (
    <th
      scope="col"
      className="border-b border-app-border px-3 py-2 text-left font-semibold text-app-hint"
    >
      {children}
    </th>
  )
}

function PayoutRequestsSkeleton() {
  return (
    <div className="flex flex-col gap-3">
      <div className="hidden overflow-hidden rounded-card border border-app-border sm:block">
        <div className="flex h-10 items-center gap-4 border-b border-app-border bg-app-card px-3">
          {Array.from({ length: 4 }, (_, index) => (
            <Skeleton key={`head-${index}`} className={cn('h-3', index === 0 ? 'w-32' : 'w-20')} />
          ))}
        </div>
        {Array.from({ length: 4 }, (_, rowIndex) => (
          <div
            key={`row-${rowIndex}`}
            className="flex h-14 items-center gap-4 border-b border-app-border px-3 last:border-b-0"
          >
            {Array.from({ length: 4 }, (_, colIndex) => (
              <Skeleton
                key={`cell-${rowIndex}-${colIndex}`}
                className={cn('h-3', colIndex === 0 ? 'w-32' : 'w-20')}
              />
            ))}
          </div>
        ))}
      </div>
      <div className="flex flex-col gap-2 sm:hidden">
        {Array.from({ length: 3 }, (_, index) => (
          <Skeleton key={index} className="h-[110px]" />
        ))}
      </div>
    </div>
  )
}
